import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { clsx } from '../../../ds/clsx';
import { usePortalOptional } from '../frame/PortalLayout';
import frame from '../frame/Portal.module.css';

/** Where an overview action leads inside the portal. */
export type PortalTarget =
  | { section: 'documents'; requestId?: string | undefined }
  | { section: 'questions'; intent?: 'appointment' | undefined }
  | { section: 'objects' }
  | { href: string };

function hrefOf(path: (section?: string) => string, target: PortalTarget): string {
  if ('href' in target) return target.href;
  const base = path(target.section);
  if (target.section === 'documents' && target.requestId)
    return `${base}?requestId=${encodeURIComponent(target.requestId)}`;
  if (target.section === 'questions' && target.intent) return `${base}?intent=${target.intent}`;
  return base;
}

/**
 * A button of the start page. In the portal it navigates; in the staff preview (`preview`, or outside the portal
 * frame) it renders as a disabled button with the same look.
 */
export function PortalAction({
  target,
  preview,
  variant = 'primary',
  className,
  children,
}: {
  target: PortalTarget;
  preview?: boolean | undefined;
  variant?: 'primary' | 'secondary' | 'link' | undefined;
  className?: string | undefined;
  children: ReactNode;
}) {
  const portal = usePortalOptional();
  const look = clsx(
    variant !== 'link' && 'pressable',
    variant === 'primary' ? frame.primary : variant === 'secondary' ? frame.secondary : frame.textLink,
    className,
  );
  if (preview || !portal) {
    return (
      <button type="button" className={look} disabled aria-disabled="true">
        {children}
      </button>
    );
  }
  const href = hrefOf(portal.path, target);
  if ('href' in target) {
    return (
      <a href={href} className={look}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={look}>
      {children}
    </Link>
  );
}

const NUMBER_WORDS = [
  'null',
  'eine',
  'zwei',
  'drei',
  'vier',
  'fünf',
  'sechs',
  'sieben',
  'acht',
  'neun',
  'zehn',
];

/** „Zwei" / „Eine" / „11" — the count word of „Zwei Aufgaben für Sie" (Portal 16). */
export function countWord(count: number, capitalized: boolean): string {
  const word = NUMBER_WORDS[count] ?? String(count);
  return capitalized ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}
